import { useState, useEffect, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Pencil, Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  Dash,
  localISODate,
  InlineText,
  InlineNumber,
  InlineDate,
  InlineSelect,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
  RowActions,
} from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'

// ─────────────────── Types ───────────────────
type Engagement = { id: number; code: string; name: string }

type Project = {
  id: number; code: string; engagement: number; engagement_code: string; engagement_name: string
  account_name: string | null; name: string; description: string
  billing_type: 'T_AND_M' | 'FIXED_FEE' | 'MILESTONE' | 'RETAINER' | 'NON_BILLABLE'
  budget_amount: string; budget_hours: string; nte_amount: string | null
  bill_rate_amount: string | null; bill_rate_unit: 'HOUR' | 'DAY' | ''
  start_date: string | null; end_date: string | null
  manager: number | null; manager_name: string | null
  manager_person: number | null
  currency_code: string; default_revenue_account: number | null
  status: 'DRAFT' | 'ACTIVE' | 'ON_HOLD' | 'DELIVERED' | 'CLOSED' | 'CANCELLED'
  notes: string
  phase_count: number; task_count: number; deliverable_count: number; milestone_count: number
}

type Currency = {
  id: number; code: string; name: string; symbol: string
  is_base: boolean; is_active: boolean
}

const BILLING_LABEL: Record<Project['billing_type'], string> = {
  T_AND_M: 'Time & Materials',
  FIXED_FEE: 'Fixed Fee',
  MILESTONE: 'Milestone',
  RETAINER: 'Retainer',
  NON_BILLABLE: 'Non-billable',
}

const STATUS_COLOR: Record<Project['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  ACTIVE: 'bg-blue-100 text-blue-700',
  ON_HOLD: 'bg-amber-100 text-amber-700',
  DELIVERED: 'bg-emerald-100 text-emerald-700',
  CLOSED: 'bg-slate-100 text-slate-700',
  CANCELLED: 'bg-red-100 text-red-700',
}

const PHASE_STATUS_COLOR: Record<'PLANNED' | 'ACTIVE' | 'DONE', string> = {
  PLANNED: 'bg-gray-100 text-gray-700',
  ACTIVE: 'bg-blue-100 text-blue-700',
  DONE: 'bg-emerald-100 text-emerald-700',
}

const TASK_STATUS_COLOR: Record<'TODO' | 'IN_PROGRESS' | 'BLOCKED' | 'DONE', string> = {
  TODO: 'bg-gray-100 text-gray-700',
  IN_PROGRESS: 'bg-blue-100 text-blue-700',
  BLOCKED: 'bg-red-100 text-red-700',
  DONE: 'bg-emerald-100 text-emerald-700',
}

const DELIVERABLE_STATUS_COLOR: Record<'PENDING' | 'IN_REVIEW' | 'ACCEPTED' | 'REJECTED', string> = {
  PENDING: 'bg-gray-100 text-gray-700',
  IN_REVIEW: 'bg-amber-100 text-amber-700',
  ACCEPTED: 'bg-emerald-100 text-emerald-700',
  REJECTED: 'bg-red-100 text-red-700',
}

const MILESTONE_STATUS_COLOR: Record<'PENDING' | 'ACHIEVED' | 'INVOICED', string> = {
  PENDING: 'bg-gray-100 text-gray-700',
  ACHIEVED: 'bg-blue-100 text-blue-700',
  INVOICED: 'bg-emerald-100 text-emerald-700',
}

const ASSIGNMENT_STATUS_COLOR: Record<'ACTIVE' | 'PAUSED' | 'ENDED', string> = {
  ACTIVE: 'bg-blue-100 text-blue-700',
  PAUSED: 'bg-amber-100 text-amber-700',
  ENDED: 'bg-slate-100 text-slate-700',
}

type TabKey = 'details' | 'cost' | 'phases' | 'tasks' | 'assignments' | 'deliverables' | 'milestones' | 'files'

// ─────────────────── Page ───────────────────
export default function ConsultingProjectDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const projectId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const projectQ = useQuery({
    queryKey: ['consulting', 'project', projectId],
    queryFn: async () => (await api.get<Project>(`/consulting/projects/${projectId}/`)).data,
    enabled: !!projectId,
  })
  const engagementsQ = useQuery({
    queryKey: ['consulting', 'engagements'],
    queryFn: async () => (await api.get<Engagement[]>('/consulting/engagements/')).data,
  })
  const personsQ = useQuery({
    queryKey: ['hr', 'persons'],
    queryFn: async () => (await api.get<{ id: number; full_name: string }[]>('/hr/persons/')).data,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Project>) =>
      (await api.patch(`/consulting/projects/${projectId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'project', projectId] })
      qc.invalidateQueries({ queryKey: ['consulting', 'projects'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/consulting/projects/${projectId}/`),
    onSuccess: () => {
      toast.success('Project deleted')
      qc.invalidateQueries({ queryKey: ['consulting', 'projects'] })
      navigate('/admin/consulting/projects')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')

  if (projectQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (projectQ.isError || !projectQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Project not found.</p>
          <Link to="/admin/consulting/projects" className="text-blue-600 hover:underline">← Back to projects</Link>
        </div>
      </AdminLayout>
    )
  }

  const project = projectQ.data
  const engagements = engagementsQ.data || []
  const persons = personsQ.data || []

  const saveProject = (payload: Partial<Project>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Page header — read-mostly, with inline editors on Name and Status */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/consulting/projects" className="text-muted-foreground hover:text-foreground hover:underline">← Projects</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{project.code}</div>
            <h2 className="text-2xl font-bold text-foreground">
              <InlineText value={project.name} onSave={(v) => saveProject({ name: v })} />
            </h2>
            <div className="text-sm text-muted-foreground">
              <Link to={`/admin/consulting/projects?engagement=${project.engagement}`} className="hover:underline">
                {project.engagement_code} — {project.engagement_name}
              </Link>
              {project.account_name && <span> · {project.account_name}</span>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <InlineStatus
              value={project.status}
              colorMap={STATUS_COLOR}
              options={[
                { value: 'DRAFT', label: 'Draft' },
                { value: 'ACTIVE', label: 'Active' },
                { value: 'ON_HOLD', label: 'On hold' },
                { value: 'DELIVERED', label: 'Delivered' },
                { value: 'CLOSED', label: 'Closed' },
                { value: 'CANCELLED', label: 'Cancelled' },
              ]}
              onSave={(v) => saveProject({ status: v })}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => confirm(`project ${project.code}`, () => deleteMut.mutate())}
                  className="text-destructive"
                >Delete project</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['details', 'cost', 'phases', 'tasks', 'assignments', 'deliverables', 'milestones', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'details' && 'Details'}
              {t === 'cost' && 'Cost'}
              {t === 'phases' && `Phases (${project.phase_count})`}
              {t === 'tasks' && `Tasks (${project.task_count})`}
              {t === 'assignments' && 'Assignments'}
              {t === 'deliverables' && `Deliverables (${project.deliverable_count})`}
              {t === 'milestones' && `Milestones (${project.milestone_count})`}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && <DetailsTab project={project} engagements={engagements} persons={persons} onSave={saveProject} />}
      {tab === 'cost' && <CostTab project={project} onSave={saveProject} />}
      {tab === 'phases' && <PhasesTab projectId={project.id} />}
      {tab === 'tasks' && <TasksTab projectId={project.id} persons={persons} />}
      {tab === 'assignments' && <AssignmentsTab projectId={project.id} projectCurrency={project.currency_code} />}
      {tab === 'deliverables' && <DeliverablesTab projectId={project.id} />}
      {tab === 'milestones' && <MilestonesTab projectId={project.id} />}
      {tab === 'files' && <FilesTab parent="consulting.project" parentId={projectId} />}
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  project, engagements, persons, onSave,
}: {
  project: Project
  engagements: Engagement[]
  persons: { id: number; full_name: string }[]
  onSave: (payload: Partial<Project>) => Promise<void>
}) {
  const [engOpen, setEngOpen] = useState(false)
  const [engDraft, setEngDraft] = useState(project.engagement)
  const [engPending, setEngPending] = useState(false)
  useEffect(() => { if (engOpen) setEngDraft(project.engagement) }, [engOpen, project.engagement])

  async function saveEngagement() {
    setEngPending(true)
    try { await onSave({ engagement: engDraft }); toast.success('Engagement updated'); setEngOpen(false) }
    catch (e) { toast.error(extractErr(e)) }
    finally { setEngPending(false) }
  }

  return (
    <div className="bg-card rounded-lg shadow p-6 max-w-3xl">
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        <Row label="Project name">
          <InlineText value={project.name} onSave={(v) => onSave({ name: v })} />
        </Row>
        <Row label="Engagement">
          <button
            type="button"
            onClick={() => setEngOpen(true)}
            className="group inline-flex items-center gap-1.5 rounded px-1 -mx-1 hover:bg-muted/60 text-left"
          >
            <span>{project.engagement_code} — {project.engagement_name}</span>
            <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100" />
          </button>
        </Row>
        <Row label="Billing type">
          <InlineSelect
            value={project.billing_type}
            options={(Object.keys(BILLING_LABEL) as Project['billing_type'][]).map((k) => ({
              value: k, label: BILLING_LABEL[k],
            }))}
            onSave={(v) => onSave({ billing_type: v as Project['billing_type'] })}
            helper="Drives where rates and NTE apply on the Cost tab."
          />
        </Row>
        <Row label="Description">
          <InlineText
            value={project.description}
            multiline
            onSave={(v) => onSave({ description: v })}
            placeholder="Add a description"
          />
        </Row>
        <Row label="Start date">
          <InlineDate value={project.start_date} onSave={(v) => onSave({ start_date: v })} />
        </Row>
        <Row label="End date">
          <InlineDate value={project.end_date} onSave={(v) => onSave({ end_date: v })} />
        </Row>
        <Row label="Manager">
          <InlineSelect
            value={project.manager_person ? String(project.manager_person) : ''}
            displayLabel={project.manager_name || <Dash />}
            allowClear
            options={persons.map((p) => ({
              value: String(p.id),
              label: p.full_name,
              searchText: p.full_name,
            }))}
            onSave={(v) => onSave({ manager_person: v ? Number(v) : null })}
          />
        </Row>
        <Row label="Status">
          <InlineStatus
            value={project.status}
            colorMap={STATUS_COLOR}
            options={[
              { value: 'DRAFT', label: 'Draft' },
              { value: 'ACTIVE', label: 'Active' },
              { value: 'ON_HOLD', label: 'On hold' },
              { value: 'DELIVERED', label: 'Delivered' },
              { value: 'CLOSED', label: 'Closed' },
              { value: 'CANCELLED', label: 'Cancelled' },
            ]}
            onSave={(v) => onSave({ status: v })}
          />
        </Row>
        <Row label="Notes">
          <InlineText
            value={project.notes}
            multiline
            onSave={(v) => onSave({ notes: v })}
            placeholder="Add notes"
          />
        </Row>
      </dl>

      <RecordDrawer
        open={engOpen}
        onOpenChange={setEngOpen}
        title="Change engagement"
        description="Move this project under a different engagement. Time entries, invoices, and assignments stay attached to the project."
        onSubmit={saveEngagement}
        isPending={engPending}
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="eng-pick">Engagement</FieldLabel>
            <SearchableSelect
              id="eng-pick"
              value={String(engDraft)}
              onChange={(v) => setEngDraft(Number(v))}
              options={engagements.map((eng) => ({
                value: String(eng.id), label: eng.name, hint: eng.code,
              }))}
            />
            <FieldDescription>
              Re-parenting is a heavy operation. Double-check the new engagement is correct.
            </FieldDescription>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

// ─────────────────── Cost tab ───────────────────

const HOURS_PER_DAY = 8

function CostTab({
  project, onSave,
}: {
  project: Project
  onSave: (payload: Partial<Project>) => Promise<void>
}) {
  const isTAndM = project.billing_type === 'T_AND_M'

  const { data: currencies = [] } = useQuery({
    queryKey: ['finance', 'currencies'],
    queryFn: async () => (await api.get<Currency[]>('/finance/currencies/')).data,
  })
  const { data: assignments = [] } = useQuery({
    queryKey: ['consulting', 'assignments', project.id],
    queryFn: async () => (await api.get<Assignment[]>(`/consulting/assignments/?project=${project.id}`)).data,
  })

  const [brOpen, setBrOpen] = useState(false)
  const [brDraft, setBrDraft] = useState({
    bill_rate_amount: project.bill_rate_amount || '',
    bill_rate_unit: (project.bill_rate_unit || 'HOUR') as 'HOUR' | 'DAY',
  })
  const [brPending, setBrPending] = useState(false)
  useEffect(() => {
    if (brOpen) {
      setBrDraft({
        bill_rate_amount: project.bill_rate_amount || '',
        bill_rate_unit: (project.bill_rate_unit || 'HOUR') as 'HOUR' | 'DAY',
      })
    }
  }, [brOpen, project.bill_rate_amount, project.bill_rate_unit])

  async function saveBillRate() {
    setBrPending(true)
    try {
      await onSave({
        bill_rate_amount: brDraft.bill_rate_amount === '' ? null : brDraft.bill_rate_amount,
        bill_rate_unit: brDraft.bill_rate_amount === '' ? '' : brDraft.bill_rate_unit,
      } as Partial<Project>)
      toast.success('Bill rate saved'); setBrOpen(false)
    } catch (e) { toast.error(extractErr(e)) }
    finally { setBrPending(false) }
  }

  const billRatePerHour = (() => {
    const amt = parseFloat(project.bill_rate_amount || '0')
    if (!isTAndM || !amt || isNaN(amt)) return null
    return project.bill_rate_unit === 'DAY' ? amt / HOURS_PER_DAY : amt
  })()
  const activeAssignments = assignments.filter((a) => a.status === 'ACTIVE')
  const avgCostPerHour = (() => {
    if (activeAssignments.length === 0) return null
    const rates = activeAssignments.map((a) => parseFloat(a.effective_rate_per_hour)).filter((r) => !isNaN(r) && r > 0)
    if (rates.length === 0) return null
    return rates.reduce((s, r) => s + r, 0) / rates.length
  })()
  const margin = (billRatePerHour !== null && avgCostPerHour !== null) ? billRatePerHour - avgCostPerHour : null
  const marginPct = (margin !== null && billRatePerHour !== null && billRatePerHour > 0) ? (margin / billRatePerHour) * 100 : null

  const currencyOptions = currencies.filter((c) => c.is_active)
  const orphan = project.currency_code && !currencyOptions.some((c) => c.code === project.currency_code)

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Budget block */}
      <section className="bg-card rounded-lg shadow p-6">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-4">Budget &amp; currency</h3>
        <div className="grid grid-cols-3 gap-4">
          <Tile label="Currency">
            <InlineSelect
              value={project.currency_code}
              options={[
                ...(orphan ? [{ value: project.currency_code, label: `${project.currency_code} (not in finance list)` }] : []),
                ...currencyOptions.map((c) => ({
                  value: c.code,
                  label: `${c.code} — ${c.name}${c.symbol ? ` (${c.symbol})` : ''}`,
                  searchText: `${c.code} ${c.name}`,
                })),
              ]}
              onSave={(v) => onSave({ currency_code: v })}
              helper="From Finance → Currencies."
              displayLabel={<span className="font-mono">{project.currency_code}</span>}
            />
          </Tile>
          <Tile label="Budget amount">
            <InlineNumber
              value={project.budget_amount}
              onSave={(v) => onSave({ budget_amount: v })}
              suffix={project.currency_code}
              displayFormat={(v) => v ? <span className="font-mono">{Number(v).toFixed(2)} {project.currency_code}</span> : <Dash />}
            />
          </Tile>
          <Tile label="Budget hours">
            <InlineNumber
              value={project.budget_hours}
              step="0.5"
              onSave={(v) => onSave({ budget_hours: v })}
              suffix="h"
              displayFormat={(v) => v ? <span className="font-mono">{Number(v).toFixed(1)} h</span> : <Dash />}
            />
          </Tile>
        </div>
        <div className="mt-4">
          <Tile label="Not-to-exceed cap">
            {isTAndM ? (
              <InlineNumber
                value={project.nte_amount || ''}
                onSave={(v) => onSave({ nte_amount: v === '' ? null : v } as Partial<Project>)}
                displayFormat={(v) => v ? <span className="font-mono">{Number(v).toFixed(2)} {project.currency_code}</span> : <Dash />}
                helper="T&M only. Caps the cumulative bill regardless of hours logged."
              />
            ) : (
              <span className="text-muted-foreground text-sm">N/A — T&amp;M only</span>
            )}
          </Tile>
        </div>
      </section>

      {/* Bill rate block — T&M only */}
      {isTAndM ? (
        <section className="bg-card rounded-lg shadow p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Bill rate to client</h3>
              <p className="text-xs text-muted-foreground mt-1">Default rate for this T&amp;M project. Per-consultant overrides live on the Assignments tab.</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setBrOpen(true)}>
              <Pencil className="h-3 w-3 mr-1" /> Edit
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Tile label="Amount">
              {project.bill_rate_amount ? (
                <span className="font-mono">{Number(project.bill_rate_amount).toFixed(2)} {project.currency_code}</span>
              ) : <Dash />}
            </Tile>
            <Tile label="Per">
              {project.bill_rate_amount ? (
                <span>{project.bill_rate_unit === 'DAY' ? `Day (${HOURS_PER_DAY}h)` : 'Hour'}</span>
              ) : <Dash />}
            </Tile>
            <Tile label="Effective">
              {billRatePerHour !== null ? (
                <span className="font-mono">{billRatePerHour.toFixed(2)} {project.currency_code} / hour</span>
              ) : <Dash />}
            </Tile>
          </div>

          <div className="border-t pt-4 mt-6">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-3">Margin (vs. assignments)</h4>
            <MarginSummary
              billRatePerHour={billRatePerHour}
              avgCostPerHour={avgCostPerHour}
              activeAssignmentCount={activeAssignments.length}
              currency={project.currency_code}
              margin={margin}
              marginPct={marginPct}
            />
          </div>

          <RecordDrawer
            open={brOpen}
            onOpenChange={setBrOpen}
            title="Edit bill rate"
            description="Default rate charged to the client for this T&M project. Per-consultant ProjectAssignment overrides take precedence."
            onSubmit={saveBillRate}
            isPending={brPending}
          >
            <FieldGroup>
              <div className="grid grid-cols-2 gap-4">
                <Field>
                  <FieldLabel htmlFor="br-amount">Amount</FieldLabel>
                  <Input
                    id="br-amount" type="number" step="0.01" min="0"
                    value={brDraft.bill_rate_amount}
                    onChange={(e) => setBrDraft({ ...brDraft, bill_rate_amount: e.target.value })}
                    placeholder="e.g. 150.00"
                    className="font-mono text-right"
                  />
                  <FieldDescription>Leave empty to clear the bill rate.</FieldDescription>
                </Field>
                <Field>
                  <FieldLabel htmlFor="br-unit">Per</FieldLabel>
                  <Select
                    value={brDraft.bill_rate_unit}
                    onValueChange={(v) => setBrDraft({ ...brDraft, bill_rate_unit: v as 'HOUR' | 'DAY' })}
                  >
                    <SelectTrigger id="br-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HOUR">Hour</SelectItem>
                      <SelectItem value="DAY">Day ({HOURS_PER_DAY} hours)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </FieldGroup>
          </RecordDrawer>
        </section>
      ) : (
        <section className="bg-muted/50 border rounded-lg p-4 text-sm text-muted-foreground">
          Bill rate is only meaningful for Time &amp; Materials projects. Change <strong>Billing type</strong> on the Details tab to <em>Time &amp; Materials</em> to set one.
        </section>
      )}
    </div>
  )
}

function Tile({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="bg-muted/40 rounded p-3">
      <div className="text-xs font-semibold text-muted-foreground uppercase">{label}</div>
      <div className="text-base font-semibold mt-1">{children}</div>
    </div>
  )
}

function MarginSummary({
  billRatePerHour, avgCostPerHour, activeAssignmentCount, currency, margin, marginPct,
}: {
  billRatePerHour: number | null
  avgCostPerHour: number | null
  activeAssignmentCount: number
  currency: string
  margin: number | null
  marginPct: number | null
}) {
  if (billRatePerHour === null) {
    return <p className="text-sm text-muted-foreground">Set a bill rate above to see the margin against assignments.</p>
  }
  if (avgCostPerHour === null) {
    return <p className="text-sm text-muted-foreground">No active assignments yet. Once consultants are assigned with a rate, margin will appear here.</p>
  }
  const marginColor = margin !== null && margin < 0 ? 'text-red-700'
    : margin !== null && margin < billRatePerHour * 0.15 ? 'text-amber-700' : 'text-green-700'
  return (
    <div className="grid grid-cols-3 gap-4 text-sm">
      <Tile label="Bill rate">
        <span className="font-mono">{billRatePerHour.toFixed(2)} {currency} / hour</span>
      </Tile>
      <Tile label="Avg cost rate">
        <div className="font-mono">{avgCostPerHour.toFixed(2)} {currency} / hour</div>
        <div className="text-xs text-muted-foreground mt-1 font-normal">
          across {activeAssignmentCount} active assignment{activeAssignmentCount === 1 ? '' : 's'}
        </div>
      </Tile>
      <Tile label="Margin">
        <span className={`font-mono ${marginColor}`}>
          {margin!.toFixed(2)} {currency} / hour
          {marginPct !== null && <span className="text-xs ml-1">({marginPct.toFixed(1)}%)</span>}
        </span>
      </Tile>
    </div>
  )
}

// ─────────────────── Phases tab ───────────────────
type Phase = {
  id: number; project: number; name: string; description: string; order: number
  planned_start: string | null; planned_end: string | null
  status: 'PLANNED' | 'ACTIVE' | 'DONE'; task_count: number
}

function PhasesTab({ projectId }: { projectId: number }) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()
  const phasesQ = useQuery({
    queryKey: ['consulting', 'phases', projectId],
    queryFn: async () => (await api.get<Phase[]>(`/consulting/phases/?project=${projectId}`)).data,
  })

  const [editing, setEditing] = useState<Phase | null>(null)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<Partial<Phase>>({})

  function openCreate() {
    setEditing(null)
    setDraft({ name: '', planned_start: null, planned_end: null, status: 'PLANNED' })
    setCreating(true)
  }
  function openEdit(p: Phase) {
    setCreating(false); setEditing(p); setDraft({ ...p })
  }
  function closeDrawer() { setCreating(false); setEditing(null) }

  const createMut = useMutation({
    mutationFn: async () => (await api.post('/consulting/phases/', {
      project: projectId,
      name: draft.name, description: draft.description || '',
      order: (phasesQ.data?.length || 0) + 1,
      planned_start: draft.planned_start || null,
      planned_end: draft.planned_end || null,
      status: draft.status || 'PLANNED',
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'phases', projectId] })
      qc.invalidateQueries({ queryKey: ['consulting', 'project', projectId] })
      toast.success('Phase added'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const editMut = useMutation({
    mutationFn: async () => (await api.patch(`/consulting/phases/${editing!.id}/`, {
      name: draft.name, description: draft.description || '',
      planned_start: draft.planned_start || null,
      planned_end: draft.planned_end || null,
      status: draft.status,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'phases', projectId] })
      toast.success('Phase saved'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const inlineMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<Phase> }) =>
      (await api.patch(`/consulting/phases/${id}/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consulting', 'phases', projectId] }),
  })
  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/consulting/phases/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'phases', projectId] })
      qc.invalidateQueries({ queryKey: ['consulting', 'project', projectId] })
      toast.success('Phase deleted')
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const saveField = async (id: number, payload: Partial<Phase>) => {
    await inlineMut.mutateAsync({ id, payload })
  }

  const phases = phasesQ.data || []

  return (
    <div className="space-y-4">
      {dialog}
      <div className="flex justify-end">
        <Button size="sm" onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add phase</Button>
      </div>

      {phases.length === 0 ? (
        <p className="text-muted-foreground text-sm">No phases yet.</p>
      ) : (
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted/40">
            <tr><Th>#</Th><Th>Name</Th><Th>Start</Th><Th>End</Th><Th>Status</Th><Th className="text-right">Tasks</Th><Th /></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {phases.map((p) => (
              <tr key={p.id}>
                <Td className="text-xs">{p.order}</Td>
                <Td className="font-medium">
                  <InlineText value={p.name} onSave={(v) => saveField(p.id, { name: v })} />
                </Td>
                <Td className="text-xs">
                  <InlineDate value={p.planned_start} onSave={(v) => saveField(p.id, { planned_start: v })} />
                </Td>
                <Td className="text-xs">
                  <InlineDate value={p.planned_end} onSave={(v) => saveField(p.id, { planned_end: v })} />
                </Td>
                <Td>
                  <InlineStatus
                    value={p.status}
                    colorMap={PHASE_STATUS_COLOR}
                    options={[
                      { value: 'PLANNED', label: 'Planned' },
                      { value: 'ACTIVE', label: 'Active' },
                      { value: 'DONE', label: 'Done' },
                    ]}
                    onSave={(v) => saveField(p.id, { status: v })}
                  />
                </Td>
                <Td className="text-right text-xs text-muted-foreground">{p.task_count}</Td>
                <Td className="text-right">
                  <RowActions
                    onEdit={() => openEdit(p)}
                    onDelete={() => confirm(`phase "${p.name}"`, () => deleteMut.mutate(p.id))}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <RecordDrawer
        open={creating || editing !== null}
        onOpenChange={(v) => { if (!v) closeDrawer() }}
        title={editing ? 'Edit phase' : 'New phase'}
        description={editing ? 'Update phase details.' : 'Add a new phase to this project.'}
        onSubmit={() => editing ? editMut.mutate() : createMut.mutate()}
        isPending={createMut.isPending || editMut.isPending}
        submitLabel={editing ? 'Save' : 'Add phase'}
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="ph-name">Phase name *</FieldLabel>
            <Input id="ph-name" autoFocus required value={draft.name || ''}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </Field>
          <Field>
            <FieldLabel htmlFor="ph-desc">Description</FieldLabel>
            <Textarea id="ph-desc" rows={3} value={draft.description || ''}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="ph-start">Planned start</FieldLabel>
              <DatePicker id="ph-start" value={draft.planned_start || ''}
                onChange={(v) => setDraft({ ...draft, planned_start: v || null })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="ph-end">Planned end</FieldLabel>
              <DatePicker id="ph-end" value={draft.planned_end || ''}
                onChange={(v) => setDraft({ ...draft, planned_end: v || null })} />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="ph-status">Status</FieldLabel>
            <Select value={draft.status || 'PLANNED'}
              onValueChange={(v) => setDraft({ ...draft, status: v as Phase['status'] })}>
              <SelectTrigger id="ph-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PLANNED">Planned</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="DONE">Done</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

// ─────────────────── Tasks tab ───────────────────
type Task = {
  id: number; project: number; phase: number | null; phase_name: string | null
  name: string; description?: string; billable: boolean; budget_hours: string; order: number
  status: 'TODO' | 'IN_PROGRESS' | 'BLOCKED' | 'DONE'
  assigned_to_person: number | null; assigned_to_name: string | null
}

function TasksTab({ projectId, persons }: { projectId: number; persons: { id: number; full_name: string }[] }) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()
  const tasksQ = useQuery({
    queryKey: ['consulting', 'tasks', projectId],
    queryFn: async () => (await api.get<Task[]>(`/consulting/tasks/?project=${projectId}`)).data,
  })
  const phasesQ = useQuery({
    queryKey: ['consulting', 'phases', projectId],
    queryFn: async () => (await api.get<Phase[]>(`/consulting/phases/?project=${projectId}`)).data,
  })

  const [editing, setEditing] = useState<Task | null>(null)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<Partial<Task>>({})

  function openCreate() {
    setEditing(null)
    setDraft({ name: '', phase: null, assigned_to_person: null, budget_hours: '0', billable: true, status: 'TODO' })
    setCreating(true)
  }
  function openEdit(t: Task) { setCreating(false); setEditing(t); setDraft({ ...t }) }
  function closeDrawer() { setCreating(false); setEditing(null) }

  const createMut = useMutation({
    mutationFn: async () => (await api.post('/consulting/tasks/', {
      project: projectId, name: draft.name, description: draft.description || '',
      phase: draft.phase || null,
      billable: draft.billable ?? true,
      budget_hours: draft.budget_hours || '0',
      assigned_to_person: draft.assigned_to_person || null,
      order: (tasksQ.data?.length || 0) + 1,
      status: draft.status || 'TODO',
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'tasks', projectId] })
      qc.invalidateQueries({ queryKey: ['consulting', 'project', projectId] })
      toast.success('Task added'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const editMut = useMutation({
    mutationFn: async () => (await api.patch(`/consulting/tasks/${editing!.id}/`, {
      name: draft.name, description: draft.description || '',
      phase: draft.phase || null,
      billable: draft.billable,
      budget_hours: draft.budget_hours,
      assigned_to_person: draft.assigned_to_person || null,
      status: draft.status,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'tasks', projectId] })
      toast.success('Task saved'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const inlineMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<Task> }) =>
      (await api.patch(`/consulting/tasks/${id}/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consulting', 'tasks', projectId] }),
  })
  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/consulting/tasks/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'tasks', projectId] })
      qc.invalidateQueries({ queryKey: ['consulting', 'project', projectId] })
      toast.success('Task deleted')
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const saveField = async (id: number, payload: Partial<Task>) => {
    await inlineMut.mutateAsync({ id, payload })
  }

  const tasks = tasksQ.data || []
  const phases = phasesQ.data || []

  return (
    <div className="space-y-4">
      {dialog}
      <div className="flex justify-end">
        <Button size="sm" onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add task</Button>
      </div>

      {tasks.length === 0 ? (
        <p className="text-muted-foreground text-sm">No tasks yet.</p>
      ) : (
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted/40">
            <tr><Th>Name</Th><Th>Phase</Th><Th>Assigned</Th><Th className="text-right">Hours</Th><Th>Billable</Th><Th>Status</Th><Th /></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {tasks.map((t) => (
              <tr key={t.id}>
                <Td className="font-medium">
                  <InlineText value={t.name} onSave={(v) => saveField(t.id, { name: v })} />
                </Td>
                <Td className="text-xs">
                  <InlineSelect
                    value={t.phase ? String(t.phase) : ''}
                    displayLabel={t.phase_name || <Dash />}
                    allowClear
                    options={phases.map((p) => ({ value: String(p.id), label: p.name, searchText: p.name }))}
                    onSave={(v) => saveField(t.id, { phase: v ? Number(v) : null })}
                  />
                </Td>
                <Td className="text-xs">
                  <InlineSelect
                    value={t.assigned_to_person ? String(t.assigned_to_person) : ''}
                    displayLabel={t.assigned_to_name || <Dash />}
                    allowClear
                    options={persons.map((p) => ({ value: String(p.id), label: p.full_name, searchText: p.full_name }))}
                    onSave={(v) => saveField(t.id, { assigned_to_person: v ? Number(v) : null })}
                  />
                </Td>
                <Td className="text-right">
                  <InlineNumber
                    value={t.budget_hours}
                    step="0.5"
                    onSave={(v) => saveField(t.id, { budget_hours: v })}
                    displayFormat={(v) => <span className="font-mono text-xs">{Number(v || 0).toFixed(1)}</span>}
                  />
                </Td>
                <Td>
                  <InlineStatus
                    value={t.billable ? 'BILLABLE' : 'NON_BILLABLE'}
                    colorMap={{
                      BILLABLE: 'bg-blue-100 text-blue-700',
                      NON_BILLABLE: 'bg-gray-100 text-gray-700',
                    }}
                    options={[
                      { value: 'BILLABLE' as const, label: 'Billable' },
                      { value: 'NON_BILLABLE' as const, label: 'Non-billable' },
                    ]}
                    onSave={(v) => saveField(t.id, { billable: v === 'BILLABLE' })}
                  />
                </Td>
                <Td>
                  <InlineStatus
                    value={t.status}
                    colorMap={TASK_STATUS_COLOR}
                    options={[
                      { value: 'TODO', label: 'To do' },
                      { value: 'IN_PROGRESS', label: 'In progress' },
                      { value: 'BLOCKED', label: 'Blocked' },
                      { value: 'DONE', label: 'Done' },
                    ]}
                    onSave={(v) => saveField(t.id, { status: v })}
                  />
                </Td>
                <Td className="text-right">
                  <RowActions
                    onEdit={() => openEdit(t)}
                    onDelete={() => confirm(`task "${t.name}"`, () => deleteMut.mutate(t.id))}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <RecordDrawer
        open={creating || editing !== null}
        onOpenChange={(v) => { if (!v) closeDrawer() }}
        title={editing ? 'Edit task' : 'New task'}
        description={editing ? 'Update task details.' : 'Add a new task to this project.'}
        onSubmit={() => editing ? editMut.mutate() : createMut.mutate()}
        isPending={createMut.isPending || editMut.isPending}
        submitLabel={editing ? 'Save' : 'Add task'}
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="tk-name">Task name *</FieldLabel>
            <Input id="tk-name" autoFocus required value={draft.name || ''}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </Field>
          <Field>
            <FieldLabel htmlFor="tk-desc">Description</FieldLabel>
            <Textarea id="tk-desc" rows={3} value={draft.description || ''}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="tk-phase">Phase</FieldLabel>
              <SearchableSelect
                id="tk-phase"
                value={draft.phase ? String(draft.phase) : NONE}
                onChange={(v) => setDraft({ ...draft, phase: v === NONE ? null : Number(v) })}
                placeholder="— none —"
                options={[
                  { value: NONE, label: '— none —' },
                  ...phases.map((p) => ({ value: String(p.id), label: p.name })),
                ]}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="tk-assigned">Assigned to</FieldLabel>
              <SearchableSelect
                id="tk-assigned"
                value={draft.assigned_to_person ? String(draft.assigned_to_person) : NONE}
                onChange={(v) => setDraft({ ...draft, assigned_to_person: v === NONE ? null : Number(v) })}
                placeholder="— none —"
                options={[
                  { value: NONE, label: '— none —' },
                  ...persons.map((p) => ({ value: String(p.id), label: p.full_name })),
                ]}
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="tk-hours">Budget hours</FieldLabel>
              <Input id="tk-hours" type="number" step="0.5" value={draft.budget_hours || '0'}
                onChange={(e) => setDraft({ ...draft, budget_hours: e.target.value })} className="font-mono text-right" />
            </Field>
            <Field>
              <FieldLabel htmlFor="tk-status">Status</FieldLabel>
              <Select value={draft.status || 'TODO'}
                onValueChange={(v) => setDraft({ ...draft, status: v as Task['status'] })}>
                <SelectTrigger id="tk-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="TODO">To do</SelectItem>
                  <SelectItem value="IN_PROGRESS">In progress</SelectItem>
                  <SelectItem value="BLOCKED">Blocked</SelectItem>
                  <SelectItem value="DONE">Done</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.billable ?? true}
                onChange={(e) => setDraft({ ...draft, billable: e.target.checked })} />
              Billable
            </label>
            <FieldDescription>Unbillable tasks don't roll up to the client invoice.</FieldDescription>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

// ─────────────────── Assignments tab ───────────────────
type WorkerLite = {
  id: number
  person_name: string
  display_number: string
  record_type: 'EMPLOYMENT' | 'CONTRACTOR'
  job_title: string | null
  is_currently_active: boolean
}

type Assignment = {
  id: number
  project: number; project_code: string; project_name: string
  worker: number
  person_name: string
  worker_display_number: string
  worker_record_type: 'EMPLOYMENT' | 'CONTRACTOR'
  employer_partner_name: string | null
  role: 'CONSULTANT' | 'CONTRACTOR' | 'MANAGER'
  vendor_contract: number | null; vendor_contract_number: string | null
  rate_override: string | null; currency_code: string
  effective_rate: string
  effective_rate_unit: 'HOUR' | 'DAY'
  effective_rate_per_hour: string
  effective_currency: string
  max_hours: string | null; hours_logged: string; hours_approved: string
  start_date: string; end_date: string | null
  status: 'ACTIVE' | 'PAUSED' | 'ENDED'; notes: string
}

type VendorContractLite = {
  id: number; contract_number: string; partner: number; title: string
  status: string; default_rate: string; currency_code: string
}

function AssignmentsTab({
  projectId, projectCurrency,
}: {
  projectId: number; projectCurrency: string
}) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()
  const { data: assignments = [], isLoading } = useQuery({
    queryKey: ['consulting', 'assignments', projectId],
    queryFn: async () => (await api.get<Assignment[]>(`/consulting/assignments/?project=${projectId}`)).data,
  })
  const { data: vendorContracts = [] } = useQuery({
    queryKey: ['finance', 'vendor-contracts'],
    queryFn: async () => (await api.get<VendorContractLite[]>('/partners/vendor-contracts/')).data,
  })
  const { data: workersAll = [] } = useQuery({
    queryKey: ['hr', 'worker-records'],
    queryFn: async () => (await api.get<WorkerLite[]>('/hr/worker-records/')).data,
  })
  // Only currently-active workers can take a new assignment.
  const workers = workersAll.filter((w) => w.is_currently_active)

  const [editing, setEditing] = useState<Assignment | null>(null)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<any>({})

  function openCreate() {
    setEditing(null)
    setDraft({
      worker: 0, role: 'CONSULTANT', vendor_contract: null,
      rate_override: '', currency_code: '',
      max_hours: '', start_date: localISODate(new Date()),
      end_date: '', notes: '',
    })
    setCreating(true)
  }
  function openEdit(a: Assignment) {
    setCreating(false); setEditing(a)
    setDraft({
      worker: a.worker, role: a.role, vendor_contract: a.vendor_contract,
      rate_override: a.rate_override || '', currency_code: a.currency_code,
      max_hours: a.max_hours || '', start_date: a.start_date,
      end_date: a.end_date || '', notes: a.notes || '',
    })
  }
  function closeDrawer() { setCreating(false); setEditing(null) }

  const createMut = useMutation({
    mutationFn: async () => (await api.post('/consulting/assignments/', {
      project: projectId, worker: draft.worker, role: draft.role,
      vendor_contract: draft.vendor_contract,
      rate_override: draft.rate_override || null,
      currency_code: draft.currency_code,
      max_hours: draft.max_hours || null,
      start_date: draft.start_date,
      end_date: draft.end_date || null,
      notes: draft.notes,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'assignments', projectId] })
      toast.success('Assignment added'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const editMut = useMutation({
    mutationFn: async () => (await api.patch(`/consulting/assignments/${editing!.id}/`, {
      role: draft.role,
      vendor_contract: draft.vendor_contract,
      rate_override: draft.rate_override || null,
      currency_code: draft.currency_code,
      max_hours: draft.max_hours || null,
      start_date: draft.start_date,
      end_date: draft.end_date || null,
      notes: draft.notes,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'assignments', projectId] })
      toast.success('Assignment saved'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const inlineMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<Assignment> }) =>
      (await api.patch(`/consulting/assignments/${id}/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consulting', 'assignments', projectId] }),
  })
  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/consulting/assignments/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'assignments', projectId] })
      toast.success('Assignment removed')
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const saveField = async (id: number, payload: Partial<Assignment>) => {
    await inlineMut.mutateAsync({ id, payload })
  }

  return (
    <div className="space-y-4">
      {dialog}
      <div className="flex items-start justify-between">
        <p className="text-xs text-muted-foreground max-w-2xl">
          Granting an assignment is what lets a consultant or contractor submit Work Reports against this project.
        </p>
        <Button size="sm" onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add assignment</Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : assignments.length === 0 ? (
        <p className="text-muted-foreground text-sm">No assignments yet.</p>
      ) : (
        <div className="bg-card rounded border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <Th>Person</Th><Th>Role</Th><Th>Vendor / Contract</Th>
                <Th>Rate</Th><Th>Hours (approved / logged)</Th>
                <Th>Period</Th><Th>Status</Th><Th />
              </tr>
            </thead>
            <tbody>
              {assignments.map((a) => (
                <tr key={a.id} className="border-t border-border">
                  <Td>
                    <div className="text-sm">{a.person_name}</div>
                    <div className="text-xs text-muted-foreground">{a.worker_display_number} · {a.worker_record_type === 'CONTRACTOR' ? 'Contractor' : 'Employee'}</div>
                  </Td>
                  <Td className="text-xs">
                    <InlineSelect
                      value={a.role}
                      options={[
                        { value: 'CONSULTANT', label: 'Consultant' },
                        { value: 'CONTRACTOR', label: 'Contractor' },
                        { value: 'MANAGER', label: 'Manager' },
                      ]}
                      onSave={(v) => saveField(a.id, { role: v as Assignment['role'] })}
                    />
                  </Td>
                  <Td className="text-xs">
                    {a.employer_partner_name ? <div>{a.employer_partner_name}</div> : <Dash />}
                    {a.vendor_contract_number && <div className="font-mono text-muted-foreground">{a.vendor_contract_number}</div>}
                  </Td>
                  <Td className="text-xs">
                    <div>{a.effective_rate} {a.effective_currency} / {a.effective_rate_unit === 'DAY' ? 'day' : 'hour'}</div>
                    {a.effective_rate_unit === 'DAY' && (
                      <div className="text-muted-foreground">≈ {Number(a.effective_rate_per_hour).toFixed(2)} / hour</div>
                    )}
                  </Td>
                  <Td className="text-xs">
                    <span className="text-green-700 font-semibold">{a.hours_approved}</span>{' / '}
                    <span className="text-muted-foreground">{a.hours_logged}</span>
                    {a.max_hours && <span className="text-muted-foreground"> · cap {a.max_hours}</span>}
                  </Td>
                  <Td className="text-xs">
                    <InlineDate value={a.start_date} onSave={(v) => saveField(a.id, { start_date: v || a.start_date })} />
                    {' → '}
                    <InlineDate value={a.end_date} onSave={(v) => saveField(a.id, { end_date: v })} />
                  </Td>
                  <Td>
                    <InlineStatus
                      value={a.status}
                      colorMap={ASSIGNMENT_STATUS_COLOR}
                      options={[
                        { value: 'ACTIVE', label: 'Active' },
                        { value: 'PAUSED', label: 'Paused' },
                        { value: 'ENDED', label: 'Ended' },
                      ]}
                      onSave={(v) => saveField(a.id, { status: v })}
                    />
                  </Td>
                  <Td className="text-right">
                    <RowActions
                      onEdit={() => openEdit(a)}
                      onDelete={() => confirm(`assignment for ${a.person_name}`, () => deleteMut.mutate(a.id))}
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={creating || editing !== null}
        onOpenChange={(v) => { if (!v) closeDrawer() }}
        title={editing ? 'Edit assignment' : 'New assignment'}
        description={editing
          ? 'Update assignment details. Vendor contract changes cascade to the effective rate.'
          : 'Assign a consultant or contractor to this project.'}
        onSubmit={() => editing ? editMut.mutate() : createMut.mutate()}
        isPending={createMut.isPending || editMut.isPending}
        submitLabel={editing ? 'Save' : 'Add assignment'}
        width="wide"
      >
        <FieldGroup>
          {!editing && (
            <Field>
              <FieldLabel htmlFor="as-worker">Worker *</FieldLabel>
              <SearchableSelect
                id="as-worker"
                value={draft.worker ? String(draft.worker) : NONE}
                onChange={(v) => setDraft({ ...draft, worker: v === NONE ? 0 : Number(v) })}
                placeholder="— pick —"
                options={[
                  { value: NONE, label: '— pick —' },
                  ...workers.map((w) => ({
                    value: String(w.id),
                    label: `${w.person_name} (${w.record_type === 'CONTRACTOR' ? 'Contractor' : 'Employee'})`,
                    hint: [w.display_number, w.job_title].filter(Boolean).join(' · '),
                  })),
                ]}
              />
              <FieldDescription>
                Employees and external contractors both appear here — an external
                contractor is a worker record backed by a contractor engagement.
              </FieldDescription>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="as-role">Role</FieldLabel>
              <Select value={draft.role || 'CONSULTANT'}
                onValueChange={(v) => setDraft({ ...draft, role: v })}>
                <SelectTrigger id="as-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CONSULTANT">Consultant (internal)</SelectItem>
                  <SelectItem value="CONTRACTOR">Contractor (external)</SelectItem>
                  <SelectItem value="MANAGER">Manager / lead</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="as-vendor">Vendor contract</FieldLabel>
              <SearchableSelect
                id="as-vendor"
                value={draft.vendor_contract ? String(draft.vendor_contract) : NONE}
                onChange={(v) => setDraft({ ...draft, vendor_contract: v === NONE ? null : Number(v) })}
                placeholder="— none —"
                options={[
                  { value: NONE, label: '— none —' },
                  ...vendorContracts.map((vc) => ({
                    value: String(vc.id),
                    label: vc.contract_number,
                    hint: `${vc.default_rate} ${vc.currency_code}`,
                  })),
                ]}
              />
              <FieldDescription>Sets the cost rate for contractor assignments.</FieldDescription>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="as-rate">Rate override</FieldLabel>
              <Input id="as-rate" type="number" step="0.01" value={draft.rate_override || ''}
                onChange={(e) => setDraft({ ...draft, rate_override: e.target.value })}
                placeholder="use contract default"
                className="font-mono text-right" />
            </Field>
            <Field>
              <FieldLabel htmlFor="as-currency">Currency override</FieldLabel>
              <Input id="as-currency" value={draft.currency_code || ''}
                onChange={(e) => setDraft({ ...draft, currency_code: e.target.value.toUpperCase() })}
                maxLength={3} className="font-mono" placeholder={projectCurrency} />
              <FieldDescription>Defaults to project currency ({projectCurrency}).</FieldDescription>
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="as-max-hours">Max hours</FieldLabel>
            <Input id="as-max-hours" type="number" step="0.01"
              value={draft.max_hours || ''}
              onChange={(e) => setDraft({ ...draft, max_hours: e.target.value })}
              placeholder="uncapped" className="font-mono text-right" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="as-start">Start date *</FieldLabel>
              <DatePicker id="as-start" required
                value={draft.start_date || ''}
                onChange={(v) => setDraft({ ...draft, start_date: v })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="as-end">End date</FieldLabel>
              <DatePicker id="as-end"
                value={draft.end_date || ''}
                onChange={(v) => setDraft({ ...draft, end_date: v })} />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="as-notes">Notes</FieldLabel>
            <Textarea id="as-notes" rows={3}
              value={draft.notes || ''}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

// ─────────────────── Deliverables tab ───────────────────
type Deliverable = {
  id: number; project: number; name: string; description: string
  acceptance_criteria: string; due_date: string | null; delivered_at: string | null
  accepted_at: string | null; status: 'PENDING' | 'IN_REVIEW' | 'ACCEPTED' | 'REJECTED'
}

function DeliverablesTab({ projectId }: { projectId: number }) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()
  const dlQ = useQuery({
    queryKey: ['consulting', 'deliverables', projectId],
    queryFn: async () => (await api.get<Deliverable[]>(`/consulting/deliverables/?project=${projectId}`)).data,
  })
  const [editing, setEditing] = useState<Deliverable | null>(null)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<Partial<Deliverable>>({})

  function openCreate() {
    setEditing(null)
    setDraft({ name: '', description: '', acceptance_criteria: '', due_date: null, status: 'PENDING' })
    setCreating(true)
  }
  function openEdit(d: Deliverable) { setCreating(false); setEditing(d); setDraft({ ...d }) }
  function closeDrawer() { setCreating(false); setEditing(null) }

  const createMut = useMutation({
    mutationFn: async () => (await api.post('/consulting/deliverables/', {
      project: projectId,
      name: draft.name, description: draft.description || '',
      acceptance_criteria: draft.acceptance_criteria || '',
      due_date: draft.due_date || null,
      status: draft.status || 'PENDING',
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'deliverables', projectId] })
      qc.invalidateQueries({ queryKey: ['consulting', 'project', projectId] })
      toast.success('Deliverable added'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const editMut = useMutation({
    mutationFn: async () => (await api.patch(`/consulting/deliverables/${editing!.id}/`, {
      name: draft.name, description: draft.description || '',
      acceptance_criteria: draft.acceptance_criteria || '',
      due_date: draft.due_date || null,
      status: draft.status,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'deliverables', projectId] })
      toast.success('Deliverable saved'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const inlineMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<Deliverable> }) =>
      (await api.patch(`/consulting/deliverables/${id}/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consulting', 'deliverables', projectId] }),
  })
  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/consulting/deliverables/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'deliverables', projectId] })
      qc.invalidateQueries({ queryKey: ['consulting', 'project', projectId] })
      toast.success('Deliverable deleted')
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const saveField = async (id: number, payload: Partial<Deliverable>) => {
    await inlineMut.mutateAsync({ id, payload })
  }

  const dls = dlQ.data || []

  return (
    <div className="space-y-4">
      {dialog}
      <div className="flex justify-end">
        <Button size="sm" onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add deliverable</Button>
      </div>

      {dls.length === 0 ? (
        <p className="text-muted-foreground text-sm">No deliverables yet.</p>
      ) : (
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted/40">
            <tr><Th>Name</Th><Th>Due</Th><Th>Delivered</Th><Th>Status</Th><Th /></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {dls.map((d) => (
              <tr key={d.id}>
                <Td className="font-medium">
                  <InlineText value={d.name} onSave={(v) => saveField(d.id, { name: v })} />
                </Td>
                <Td className="text-xs">
                  <InlineDate value={d.due_date} onSave={(v) => saveField(d.id, { due_date: v })} />
                </Td>
                <Td className="text-xs">{d.delivered_at || <Dash />}</Td>
                <Td>
                  <InlineStatus
                    value={d.status}
                    colorMap={DELIVERABLE_STATUS_COLOR}
                    options={[
                      { value: 'PENDING', label: 'Pending' },
                      { value: 'IN_REVIEW', label: 'In review' },
                      { value: 'ACCEPTED', label: 'Accepted' },
                      { value: 'REJECTED', label: 'Rejected' },
                    ]}
                    onSave={(v) => saveField(d.id, { status: v })}
                  />
                </Td>
                <Td className="text-right">
                  <RowActions
                    onEdit={() => openEdit(d)}
                    onDelete={() => confirm(`deliverable "${d.name}"`, () => deleteMut.mutate(d.id))}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <RecordDrawer
        open={creating || editing !== null}
        onOpenChange={(v) => { if (!v) closeDrawer() }}
        title={editing ? 'Edit deliverable' : 'New deliverable'}
        description={editing ? 'Update deliverable details.' : 'Add a new deliverable to this project.'}
        onSubmit={() => editing ? editMut.mutate() : createMut.mutate()}
        isPending={createMut.isPending || editMut.isPending}
        submitLabel={editing ? 'Save' : 'Add deliverable'}
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="dl-name">Deliverable name *</FieldLabel>
            <Input id="dl-name" autoFocus required value={draft.name || ''}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </Field>
          <Field>
            <FieldLabel htmlFor="dl-desc">Description</FieldLabel>
            <Textarea id="dl-desc" rows={3} value={draft.description || ''}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </Field>
          <Field>
            <FieldLabel htmlFor="dl-criteria">Acceptance criteria</FieldLabel>
            <Textarea id="dl-criteria" rows={3} value={draft.acceptance_criteria || ''}
              onChange={(e) => setDraft({ ...draft, acceptance_criteria: e.target.value })} />
            <FieldDescription>What the client needs to see to mark this accepted.</FieldDescription>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="dl-due">Due date</FieldLabel>
              <DatePicker id="dl-due" value={draft.due_date || ''}
                onChange={(v) => setDraft({ ...draft, due_date: v || null })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="dl-status">Status</FieldLabel>
              <Select value={draft.status || 'PENDING'}
                onValueChange={(v) => setDraft({ ...draft, status: v as Deliverable['status'] })}>
                <SelectTrigger id="dl-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="IN_REVIEW">In review</SelectItem>
                  <SelectItem value="ACCEPTED">Accepted</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

// ─────────────────── Milestones tab ───────────────────
type Milestone = {
  id: number; project: number; name: string; description?: string
  due_date: string | null
  billable_amount: string; deliverable: number | null; deliverable_name: string | null
  achieved_at: string | null; status: 'PENDING' | 'ACHIEVED' | 'INVOICED'
}

function MilestonesTab({ projectId }: { projectId: number }) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()
  const msQ = useQuery({
    queryKey: ['consulting', 'milestones', projectId],
    queryFn: async () => (await api.get<Milestone[]>(`/consulting/milestones/?project=${projectId}`)).data,
  })
  const dlQ = useQuery({
    queryKey: ['consulting', 'deliverables', projectId],
    queryFn: async () => (await api.get<Deliverable[]>(`/consulting/deliverables/?project=${projectId}`)).data,
  })

  const [editing, setEditing] = useState<Milestone | null>(null)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<Partial<Milestone>>({})

  function openCreate() {
    setEditing(null)
    setDraft({ name: '', description: '', due_date: null, billable_amount: '0', deliverable: null, status: 'PENDING' })
    setCreating(true)
  }
  function openEdit(m: Milestone) { setCreating(false); setEditing(m); setDraft({ ...m }) }
  function closeDrawer() { setCreating(false); setEditing(null) }

  const createMut = useMutation({
    mutationFn: async () => (await api.post('/consulting/milestones/', {
      project: projectId, name: draft.name, description: draft.description || '',
      due_date: draft.due_date || null,
      billable_amount: draft.billable_amount || '0',
      deliverable: draft.deliverable || null,
      status: draft.status || 'PENDING',
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'milestones', projectId] })
      qc.invalidateQueries({ queryKey: ['consulting', 'project', projectId] })
      toast.success('Milestone added'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const editMut = useMutation({
    mutationFn: async () => (await api.patch(`/consulting/milestones/${editing!.id}/`, {
      name: draft.name, description: draft.description || '',
      due_date: draft.due_date || null,
      billable_amount: draft.billable_amount || '0',
      deliverable: draft.deliverable || null,
      status: draft.status,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'milestones', projectId] })
      toast.success('Milestone saved'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const inlineMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<Milestone> }) =>
      (await api.patch(`/consulting/milestones/${id}/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consulting', 'milestones', projectId] }),
  })
  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/consulting/milestones/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'milestones', projectId] })
      qc.invalidateQueries({ queryKey: ['consulting', 'project', projectId] })
      toast.success('Milestone deleted')
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const saveField = async (id: number, payload: Partial<Milestone>) => {
    await inlineMut.mutateAsync({ id, payload })
  }

  const ms = msQ.data || []
  const dls = dlQ.data || []

  return (
    <div className="space-y-4">
      {dialog}
      <div className="flex justify-end">
        <Button size="sm" onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add milestone</Button>
      </div>

      {ms.length === 0 ? (
        <p className="text-muted-foreground text-sm">No milestones yet.</p>
      ) : (
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted/40">
            <tr><Th>Name</Th><Th>Due</Th><Th className="text-right">Amount</Th><Th>Deliverable</Th><Th>Status</Th><Th /></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {ms.map((m) => (
              <tr key={m.id}>
                <Td className="font-medium">
                  <InlineText value={m.name} onSave={(v) => saveField(m.id, { name: v })} />
                </Td>
                <Td className="text-xs">
                  <InlineDate value={m.due_date} onSave={(v) => saveField(m.id, { due_date: v })} />
                </Td>
                <Td className="text-right">
                  <InlineNumber
                    value={m.billable_amount}
                    onSave={(v) => saveField(m.id, { billable_amount: v })}
                    displayFormat={(v) => <span className="font-mono">{Number(v || 0).toFixed(2)}</span>}
                  />
                </Td>
                <Td className="text-xs">
                  <InlineSelect
                    value={m.deliverable ? String(m.deliverable) : ''}
                    displayLabel={m.deliverable_name || <Dash />}
                    allowClear
                    options={dls.map((d) => ({ value: String(d.id), label: d.name, searchText: d.name }))}
                    onSave={(v) => saveField(m.id, { deliverable: v ? Number(v) : null })}
                  />
                </Td>
                <Td>
                  <InlineStatus
                    value={m.status}
                    colorMap={MILESTONE_STATUS_COLOR}
                    options={[
                      { value: 'PENDING', label: 'Pending' },
                      { value: 'ACHIEVED', label: 'Achieved' },
                      { value: 'INVOICED', label: 'Invoiced' },
                    ]}
                    onSave={(v) => saveField(m.id, { status: v })}
                  />
                </Td>
                <Td className="text-right">
                  <RowActions
                    onEdit={() => openEdit(m)}
                    onDelete={() => confirm(`milestone "${m.name}"`, () => deleteMut.mutate(m.id))}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <RecordDrawer
        open={creating || editing !== null}
        onOpenChange={(v) => { if (!v) closeDrawer() }}
        title={editing ? 'Edit milestone' : 'New milestone'}
        description={editing ? 'Update milestone details.' : 'Add a new billing milestone.'}
        onSubmit={() => editing ? editMut.mutate() : createMut.mutate()}
        isPending={createMut.isPending || editMut.isPending}
        submitLabel={editing ? 'Save' : 'Add milestone'}
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="ms-name">Milestone name *</FieldLabel>
            <Input id="ms-name" autoFocus required value={draft.name || ''}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </Field>
          <Field>
            <FieldLabel htmlFor="ms-desc">Description</FieldLabel>
            <Textarea id="ms-desc" rows={3} value={draft.description || ''}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="ms-due">Due date</FieldLabel>
              <DatePicker id="ms-due" value={draft.due_date || ''}
                onChange={(v) => setDraft({ ...draft, due_date: v || null })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="ms-amount">Billable amount</FieldLabel>
              <Input id="ms-amount" type="number" step="0.01"
                value={draft.billable_amount || '0'}
                onChange={(e) => setDraft({ ...draft, billable_amount: e.target.value })}
                className="font-mono text-right" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="ms-deliverable">Linked deliverable</FieldLabel>
              <SearchableSelect
                id="ms-deliverable"
                value={draft.deliverable ? String(draft.deliverable) : NONE}
                onChange={(v) => setDraft({ ...draft, deliverable: v === NONE ? null : Number(v) })}
                placeholder="— none —"
                options={[
                  { value: NONE, label: '— none —' },
                  ...dls.map((d) => ({ value: String(d.id), label: d.name })),
                ]}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="ms-status">Status</FieldLabel>
              <Select value={draft.status || 'PENDING'}
                onValueChange={(v) => setDraft({ ...draft, status: v as Milestone['status'] })}>
                <SelectTrigger id="ms-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="ACHIEVED">Achieved</SelectItem>
                  <SelectItem value="INVOICED">Invoiced</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-foreground ${className}`}>{children}</td>
}
